'use client';

import CheckIcon from '@mui/icons-material/Check';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { CommunityGameWithContent } from '@wt/app/community/types';
import { loadCommunityCompleted } from '@wt/app/community/utils';
import { useCopyToClipboard } from '@wt/shared/hooks/useCopyClipboard';
import { getSiteMode, getSiteName, getSiteURL } from '@wt/utilities/siteMode';
import Link from 'next/link';
import { browserName, isDesktop, osName } from 'react-device-detect';
import { toast } from 'react-toastify';
import useWebShare from 'react-use-web-share';

type GameTileFeatures = {
  name?: boolean;
  edit?: boolean;
  share?: boolean;
  play?: boolean;
  playedTick?: boolean;
  likes?: boolean;
  timesPlayed?: boolean;
  clickToPlay?: boolean;
  publicIcon?: boolean;
  daysPeriod?: number;
};

export const GameTile = ({
  game,
  handleClick,
  features,
}: {
  game: CommunityGameWithContent;
  handleClick: (slug: string) => void;
  features: GameTileFeatures;
}) => {
  const isPlayed = loadCommunityCompleted().includes(game.slug);

  return (
    <div
      className={`relative h-[200px] overflow-hidden rounded-lg ${features.clickToPlay ? 'cursor-pointer' : ''}`}
      onClick={() => features.clickToPlay && handleClick(game.slug)}
    >
      <img
        src={game.rounds[0].url}
        alt="Game Preview Picture"
        width="100%"
        height="100%"
        className={
          'size-full object-cover object-center' +
          (game.banned ? ' blur-xl' : '')
        }
      />
      {game.banned && (
        <div className="absolute inset-12 text-amber-400">
          <WarningRoundedIcon style={{ width: '100%', height: '100%' }} />
        </div>
      )}
      {features.name && (
        <div
          className={
            features.play
              ? 'absolute left-4 top-4 max-w-[70%] lg:max-w-[75%]'
              : 'absolute left-4 top-4 max-w-[90%]'
          }
        >
          <NameTag
            publicGame={game.public}
            name={game.name}
            showPlayedTick={!!features.playedTick && isPlayed}
            showPublicIcon={!!features.publicIcon}
          />
        </div>
      )}
      {features.play && !game.banned && (
        <div className="absolute right-4 top-4">
          <PlayButton handleClick={() => handleClick(game.slug)} />
        </div>
      )}
      {features.play && game.banned && (
        <div className="absolute right-4 top-4">
          <InfoButton slug={game.slug} />
        </div>
      )}
      {features.edit && (
        <div className="absolute bottom-4 left-4 w-[42%] lg:w-[44%]">
          <EditButton slug={game.slug} />
        </div>
      )}
      {features.share && (
        <div className="absolute bottom-4 right-4 w-[42%] lg:w-[44%]">
          <ShareButton slug={game.slug} publicGame={game.public} />
        </div>
      )}
      {(features.likes || features.timesPlayed) && (
        <div className="absolute bottom-4 left-4">
          {features.likes && (
            <span className="pointer-events-none whitespace-nowrap rounded-full bg-base-300 px-2 py-0.5 text-xs font-medium text-base-100">
              🔥{' '}
              {Math.round(
                100 * (game.likesScores['d' + (features.daysPeriod ?? 30)] ?? 0)
              )}
              %
            </span>
          )}
          {features.timesPlayed && (
            <span className="pointer-events-none ml-2 whitespace-nowrap rounded-full bg-base-300 px-2 py-0.5 text-xs font-medium text-base-100">
              ▶️{' '}
              {Math.round(
                100 *
                  (game.timesPlayedScores['d' + (features.daysPeriod ?? 30)] ??
                    0)
              )}
              %
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const NameTag = ({
  publicGame,
  name,
  showPlayedTick,
  showPublicIcon,
}: {
  publicGame: boolean;
  name: string;
  showPlayedTick: boolean;
  showPublicIcon: boolean;
}) => {
  return (
    <div className="items-center truncate rounded-full bg-secondary px-3 py-1 text-base font-semibold shadow-lg shadow-black/20 text-highlight">
      {showPublicIcon && (
        <>
          {publicGame && (
            <RemoveRedEyeOutlinedIcon sx={{ color: 'currentColor' }} />
          )}
          {!publicGame && <LockOutlinedIcon sx={{ color: 'currentColor' }} />}
        </>
      )}
      <span className={showPublicIcon ? 'ml-2 text-white' : 'text-white'}>
        {name}
      </span>
      {showPlayedTick && (
        <div className="absolute right-[-4px] top-[-4px] flex size-4 items-center justify-center rounded-full bg-primary text-xs">
          <CheckIcon className="w-10/12 fill-white" fontSize="inherit" />
        </div>
      )}
    </div>
  );
};

const PlayButton = ({ handleClick }: { handleClick: () => void }) => {
  return (
    <div
      onClick={handleClick}
      className="flex size-8 cursor-pointer items-center justify-center rounded-lg border border-[#978FA3] bg-white shadow-lg shadow-black/20 hover:border-[#322E38] hover:bg-[#F6F0FE]"
    >
      <PlayCircleOutlineRoundedIcon htmlColor="black" />
    </div>
  );
};

const InfoButton = ({ slug }: { slug: string }) => {
  return (
    <Link
      href={`/account/games/edit/${slug}`}
      className="flex size-8 cursor-pointer items-center justify-center rounded-lg border border-[#978FA3] bg-white shadow-lg shadow-black/20 hover:border-[#322E38] hover:bg-[#F6F0FE]"
    >
      <HelpOutlineIcon htmlColor="black" />
    </Link>
  );
};

const EditButton = ({ slug }: { slug: string }) => {
  return (
    <Link
      href={`/account/games/edit/${slug}`}
      className="btn h-[33px] min-h-[33px] w-full shrink rounded-lg border border-gray-400 bg-white text-sm font-medium text-[#230351] hover:bg-gray-100"
    >
      <span className="pointer-events-none whitespace-nowrap">
        <EditRoundedIcon className="pr-1" /> Edit
      </span>
    </Link>
  );
};

const ShareButton = ({
  slug,
  publicGame,
}: {
  slug: string;
  publicGame: boolean;
}) => {
  const [, copyShareText] = useCopyToClipboard();
  const { isSupported, share } = useWebShare();

  const url = `${getSiteURL()}/community/game/${slug}`;
  let shareText = `Hey, check out this #${getSiteMode()} game I created: ${url}\nDon't forget to share your score 😉`;
  if (!publicGame) {
    shareText +=
      '\nThis is an unlisted game that only people with this unique link can see.';
  }

  const shareGame = () => {
    // Firefox Android does not support navigator.share with text
    const isFirefoxAndroid = browserName === 'Firefox' && osName === 'Android';

    if (isSupported && !isFirefoxAndroid && !isDesktop) {
      share({
        title: getSiteName() + ' Community Game',
        text: shareText,
        url: url,
      });
    } else {
      copyShareText(shareText).then((c) => {
        toast(c ? 'Copied to clipboard' : 'Failed to copy to clipboard', {
          type: c ? 'success' : 'error',
        });
      });
    }
    return;
  };

  return (
    <button
      onClick={shareGame}
      className="btn btn-primary h-[33px] min-h-[33px] w-full shrink whitespace-nowrap rounded-lg text-sm font-medium"
    >
      <span className="pointer-events-none whitespace-nowrap">
        <ShareOutlinedIcon className="pr-1" /> Share
      </span>
    </button>
  );
};
